import React, { useContext, useEffect, useRef, useState } from "react";
import "./App.css";
import {
  NavigationLocation,
  NextpageContext,
  NextPageService,
  NpFooter,
  NpHeader,
  NpMenu,
  NpSearch,
  PageLayout,
  SearchType,
} from "@cvx/nextpage-react";
import { BrowserRouter, Route, Routes } from "react-router-dom";
import Home from "./pages/home/Home";
import { CalClient } from "@cvx/cal";
import { User } from "./core/interfaces/User";
import { TokenContext } from "./context/token-context";
import { UserContext } from "./context/user-context";
import { environment } from "./environments/environment";
import CCRequest from "./components/Enter Request/CCRequest";
import "antd/dist/reset.css";
import SearchForRequests from "./components/manage-request/search-requests/SearchForRequests";
import SearchResults from "./components/manage-request/search-requests/SearchResults";
import SuperAdminSetup from "./components/administration/super-admin-setup/SuperAdminSetup";
import ReviewerSetup from "./components/administration/reviewsetup/ReviewSetup";
import ReportSpecsSummary from "./components/reports/reportSpecsSummary";
import ReportSummary from "./components/reports/reportSummary";
import ManageApplication from "./components/administration/manage-application/ManageApplication";
import OpenRequests from "./components/manage-request/my-open-request/my-open-request";
import MyPendingAction from "./components/manage-request/my-pending-action/my-pending-action";
import AccRequest from "./components/Enter Request/AccountRequest";
import Help from "./pages/help/Help";
import AccountsGuide from "./pages/accountsguide/AccountsGuide";
import { appInsights } from "./core/services/insight.service";
import AccRequestDetail from "./components/acc-Request-Detail/accRequestDetail";
import { Spin } from "antd";
import ResultPage from "./components/result/ResultPage";
import { fetchSuperUsersData } from "./core/services/account-request.service";
import Title from "antd/lib/typography/Title";

const np = new NextPageService();
const cal = new CalClient(environment.calConfig);
np.UI.options.sitename = "Account Request";

function App() {
  const UI = np.UI;
  const options = np.options;
  const properties = np.properties;
  const [login, setLogin] = useState(false);
  const [accessToken, setAccessToken] = useState<any>();
  const [user, setUser] = useState<User>({});
  const [isLoading, setIsLoading] = useState(true);
  const [isSuperUser, setIsSuperUser] = useState(false);
  np.UI.layout.setLayout(PageLayout.Layout12);
  const appInitializedRef = useRef(false);

  useEffect(() => {
    // Skip effect in strict mode's second run
    if (appInitializedRef.current) return;
    async function initializeApp() {
      if (cal) {
        try {
          const result = await cal.isUserSignedIn();
          
          const access_token = await cal.getAADToken();

          if (result) {
            const userData = {
              name: cal.cvxClaimsPrincipal.name,
              provisioningId: cal.cvxClaimsPrincipal.provisioningId,
              email: cal.cvxClaimsPrincipal.email,
              objectId: cal.cvxClaimsPrincipal.objectId,
              userName: cal.cvxClaimsPrincipal.userName,
            };
            setUser(userData);
            setLogin(true);
            const superUsers = await fetchSuperUsersData(userData);
            const isSuper = superUsers.some(
              (superUser: { emailID: string }) => superUser.emailID.toLocaleLowerCase() === userData.email.toLocaleLowerCase()
            );
            setIsSuperUser(isSuper);
          } else {
            setLogin(false);
          }

          if (access_token) {
            setAccessToken(access_token);
          }
        } catch (error: any) {
          appInsights.trackEvent({ name: "IsUserLoginFailed", properties: { error: error } });
          setLogin(false);
        } finally {
          setIsLoading(false);
          appInitializedRef.current = true;
        }
      }
    }

    initializeApp();
  }, []);

  // Separate the app content to render conditionally
  const appContent = (
    <BrowserRouter>
      <TokenContext.Provider value={accessToken}>
        <UserContext.Provider value={user}>
          <NextpageContext.Provider value={{ UI, options, properties }}>
          <header>
                  <NpHeader>
                    <NpSearch searchType={SearchType.Global} />
                    <NpMenu location={NavigationLocation.Global} nextPage={np} />
                  </NpHeader>
                </header>
                <NpMenu location={NavigationLocation.SiteSpecific} nextPage={np} />
            <main className="container">
              <section id="column1">
                <Routes>
                  <Route path="/about" element={<Home />}></Route>
                  <Route path="/" element={<Home />}></Route>
                  <Route path="/home" element={<Home />}></Route>
                  <Route path="dashboards" element={<Home />}></Route>
                  <Route path="support" element={<Home />}></Route>
                  <Route path="/help" element={<Help />}></Route>
                  <Route path="/accounts-guide" element={<AccountsGuide />}></Route>
                  <Route
                    path="enter request/ccrequest"
                    element={<CCRequest />}
                  ></Route>
                  <Route
                    path="enter request/accreqeuest"
                    element={<AccRequest />}
                  ></Route>
                  <Route
                    path="manage requests/myopenrequests"
                    element={<OpenRequests />}
                  ></Route>
                  <Route
                    path="manage requests/mypendingaction"
                    element={<MyPendingAction />}
                  ></Route>
                  <Route
                    path="manage requests/searchrequests"
                    element={<SearchForRequests />}
                  ></Route>
                  <Route
                    path="manage requests/search-results"
                    element={<SearchResults />}
                  ></Route>
                  <Route
                    path="manage requests/openrequests"
                    element={<OpenRequests />}
                  ></Route>
                  <Route
                    path="administration/super-admin-setup"
                    element={
                      isSuperUser ? <SuperAdminSetup /> : 
                      <div style={{ textAlign: "center", marginTop: "50px" }}>
                      <Title level={3}>You are not authorized to access this page.</Title>
                      </div>
                    }
                  ></Route>
                  <Route
                    path="administration/review-setup"
                    element={
                      isSuperUser ? <ReviewerSetup /> : 
                      <div style={{ textAlign: "center", marginTop: "50px" }}>
                      <Title level={3}>You are not authorized to access this page.</Title>
                      </div>
                    }
                  ></Route>
                  <Route
                    path="reports/reportSpecs-summary"
                    element={isSuperUser ? <ReportSpecsSummary /> : 
                      <div style={{ textAlign: "center", marginTop: "50px" }}>
                      <Title level={3}>You are not authorized to access this page.</Title>
                      </div>}
                  ></Route>
                  <Route
                    path="/report-summary"
                    element={ isSuperUser ? <ReportSummary /> : 
                      <div style={{ textAlign: "center", marginTop: "50px" }}>
                      <Title level={3}>You are not authorized to access this page.</Title>
                      </div>}
                  ></Route>
                  <Route
                    path="administration/manage-application"
                    element={
                      isSuperUser ? <ManageApplication /> : 
                      <div style={{ textAlign: "center", marginTop: "50px" }}>
                      <Title level={3}>You are not authorized to access this page.</Title>
                      </div>
                    }
                  ></Route>
                  <Route
                    path="/manage requests/request-detail"
                    element={<AccRequestDetail />}
                  ></Route>
                  <Route
                  path="/result"
                  element={<ResultPage  />} >                
                  </Route>
                  
                </Routes>
              </section>
              <section id="column2"></section>
            </main>

            <NpFooter
              copyright={new Date().getFullYear().toString()}
              confidentiality="Company Confidential"
              platformName="NMA SBU"
              platformUrl="http://www.lagnr.chevron.com"
              lastUpdate= {process.env.REACT_APP_BUILD_DATE? process.env.REACT_APP_BUILD_DATE : ""} //"September 17th, 2017"
              contentContact="Adeolu Femi (EFEL)"
              contentContactUrl="mailto:FemiAdeolu@chevron.com?subject=Accounts Request Application Feedback"
              technicalContact="Utilities Assets Applications"
              technicalContactUrl="mailto:L9lek247-smb@chevron.com?subject=Accounts Request Application Feedback"
            />
          </NextpageContext.Provider>
        </UserContext.Provider>
      </TokenContext.Provider>
    </BrowserRouter>
  );

  return (
    <>
      <Spin spinning={isLoading && !login} size="large" fullscreen />
      {!isLoading && appContent}
    </>
  );
}

np.options.navigationRoutes = [
  {
    title: "Home",
    url: "home",
  },
  {
    title: "Enter Request",
    url: "enter request",
    children: [
      {
        title: "create business unit request",
        url: "ccrequest",
      },
      {
        title: "create account request",
        url: "accreqeuest",
      },
    ],
  },
  {
    title: "Manage Requests",
    url: "manage requests",
    children: [
      {
        title: "search for requests",
        url: "searchrequests",
      },
      {
        title: "my open requests",
        url: "openrequests",
      },
      {
        title: "requests pending my action",
        url: "mypendingaction",
      },
    ],
  },
  {
    title: "Reports",
    url: "reports",
    children: [
      {
        title: "summary report",
        url: "reportSpecs-summary",
      },
    ],
  },
  {
    title: "Administration",
    url: "administration",
    children: [
      {
        title: "reviewer setup",
        url: "review-setup",
      },
      {
        title: "super admin setup",
        url: "super-admin-setup",
      },
      {
        title: "manage application",
        url: "manage-application",
      },
    ],
  },
  {
    title: "Help",
    url: "help",
  },
  {
    title: "Accounts Guide",
    url: "accounts-guide",
  },
];

export default App;